// =============================================================================
// PTEL - Email Notification Service (T-11: migrado de EmailJS → Resend Worker)
// =============================================================================
// FB.10.05 · Notifica al admin cuando un viewer solicita rol Editor.
//             Flujo: EditorRequestForm → Worker /api/send-editor-request → Resend
// FB.10.18 · Notifica al solicitante con el resultado (aprobación/rechazo).
//             Flujo: editorRequest.service → Worker /api/send-result → Resend
//
// DISEÑO DEFENSIVO: nunca lanza excepción. El flujo NUNCA se interrumpe
// aunque el email no se envíe.
//
// SEC0-b2b · DOP-12 (b): llama a `WORKER_BASE` fijo (no a `VITE_WORKER_URL`).
//            DOP-9 (a): manda `Authorization: Bearer <idToken>`; el Worker toma
//            el nombre y el correo de quien pide de la sesión, no del cuerpo.
// =============================================================================

import { ROLE_DURATIONS } from '../../types/roles';
import type { RoleDuration } from '../../types/roles';
import { auth } from '../../config/firebase';
import { WORKER_BASE } from '@/lib/config/workerEndpoints';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

export interface EditorRequestEmailParams {
  /** Ya no se envía (SEC0-b2b DOP-9): el Worker lo toma de la sesión. */
  requesterName?: string;
  /** Ya no se envía (SEC0-b2b DOP-9): el Worker lo toma de la sesión. */
  requesterEmail?: string;
  /** Justificación de la solicitud */
  reason: string;
}

export interface EmailSendResult {
  /** true si se envió correctamente */
  success: boolean;
  /** Mensaje de error si success === false */
  error?: string;
}

/** FB.10.18 — Parámetros para notificar resultado al solicitante */
export interface RequestResultEmailParams {
  /** Email del solicitante (destinatario) */
  recipientEmail: string;
  /** Nombre del solicitante */
  recipientName: string;
  /** 'approved' o 'rejected' */
  result: 'approved' | 'rejected';
  /** Clave de duración (RoleDuration) concedida, solo si aprobado */
  duration?: RoleDuration;
  /** Motivo del rechazo (solo si rechazado) */
  rejectionReason?: string;
}

/**
 * Cabeceras con la sesión de Firebase de quien llama, o `null` si no hay
 * sesión (el Worker respondería 401). Nunca lanza.
 */
async function sessionHeaders(): Promise<Record<string, string> | null> {
  const user = auth?.currentUser;
  if (!user) return null;
  try {
    const idToken = await user.getIdToken();
    return { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` };
  } catch {
    return null;
  }
}

const SIN_SESION: EmailSendResult = { success: false, error: 'Sin sesión iniciada' };

// -----------------------------------------------------------------------------
// Flujo 4 — sendEditorRequestNotification (FB.10.05)
// -----------------------------------------------------------------------------

/**
 * Envía una notificación al Worker (Cloudflare) que consulta Firestore para
 * obtener admins y moderators dinámicamente y envía el email vía Resend.
 *
 * **Sin fallback EmailJS** (eliminado en T-11): si no hay sesión o el
 * Worker falla, devuelve { success: false } silenciosamente.
 *
 * **Contrato defensivo**: nunca lanza excepción.
 */
export async function sendEditorRequestNotification(
  params: EditorRequestEmailParams,
): Promise<EmailSendResult> {
  const headers = await sessionHeaders();
  if (!headers) return SIN_SESION;
  try {
    const res = await fetch(`${WORKER_BASE}/api/send-editor-request`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ reason: params.reason }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      const msg = (errBody as { error?: string }).error ?? `Worker HTTP ${res.status}`;
      return { success: false, error: msg };
    }
    return { success: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error de red con Worker';
    if (import.meta.env.DEV) {
      console.warn('⚠️ sendEditorRequestNotification: Worker falló:', message);
    }
    return { success: false, error: message };
  }
}

// -----------------------------------------------------------------------------
// Flujo 5 — sendRequestResultNotification (FB.10.18)
// -----------------------------------------------------------------------------

/**
 * Envía notificación al solicitante con el resultado de su solicitud
 * (aprobación o rechazo) vía Worker Cloudflare → Resend.
 *
 * La duración se traduce de clave RoleDuration a etiqueta legible antes
 * de enviar al Worker (el Worker recibe texto plano, no claves internas).
 *
 * **Contrato defensivo**: nunca lanza excepción.
 */
export async function sendRequestResultNotification(
  params: RequestResultEmailParams,
): Promise<EmailSendResult> {
  const headers = await sessionHeaders();
  if (!headers) return SIN_SESION;

  const durationLabel = params.duration
    ? (ROLE_DURATIONS[params.duration]?.label ?? params.duration)
    : undefined;

  try {
    const res = await fetch(`${WORKER_BASE}/api/send-result`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        recipientEmail: params.recipientEmail,
        recipientName: params.recipientName,
        result: params.result,
        duration: durationLabel,
        rejectionReason: params.rejectionReason,
      }),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      return {
        success: false,
        error: (errBody as { error?: string }).error ?? `Worker HTTP ${res.status}`,
      };
    }
    return { success: true };
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Error de red';
    if (import.meta.env.DEV) {
      console.warn('⚠️ sendRequestResultNotification: Worker falló:', message);
    }
    return { success: false, error: message };
  }
}
